/**
 * useClickOutside — Dismisses a dropdown/popover when clicking outside.
 *
 * Attaches a `mousedown` listener to `document` only while `isOpen` is true,
 * and calls `onClose` when the click target is outside the ref element.
 *
 * Used by: ProfilePlaylistMenuOptions, ProfileMyPlaylists (PlaylistSharingDropdown)
 */

import {
  useCallback, useEffect, useRef, type RefObject,
} from 'react'

export default function useClickOutside (
  ref: RefObject<HTMLElement | null>,
  onClose: () => void,
  isOpen: boolean,
): void {
  const onCloseRef = useRef(onClose)
  onCloseRef.current = onClose

  const handleClickOutside = useCallback((e: MouseEvent) => {
    if (e.target instanceof Node && ref.current && !ref.current.contains(e.target)) {
      onCloseRef.current()
    }
  }, [ref])

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen, handleClickOutside])
}
